import { FC, ReactNode } from "react";
import Build from "@mui/icons-material/Build";
import DashboardCustomize from "@mui/icons-material/DashboardCustomize";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import SettingsIcon from "@mui/icons-material/Settings";
import Store from "@mui/icons-material/Store";
import NewOrderBadge from "components/Badge/NewOrderBadge";
import {
  InquiryUpdatesBadge,
  RequisitionUpdatesBadge,
} from "components/Badge/StatusUpdatesBadge";
import ReviewQuoteBadge from "components/Badge/ReviewQuoteBadge";
import { ReactComponent as PartnersIcon } from "../assets/svg/handshake-outline.svg";

/** Props every sidebar badge gets. `active` is true while the page the badge
 *  belongs to is the one currently open. */
export type NavBadgeProps = { active: boolean };

export type NavPage = {
  name: string;
  path: string;
  badge?: FC<NavBadgeProps>;
};

export type NavGroup = {
  name: string;
  icon: ReactNode;
  pages: NavPage[];
};

export type NavLink = {
  name: string;
  path: string;
  icon: ReactNode;
};

export type NavEntry = NavGroup | NavLink;

export const isNavGroup = (entry: NavEntry): entry is NavGroup =>
  "pages" in entry;

const iconProps = { className: "listIcon" };

// Hvis navne skal ændres, gøres det her
export const NAV_ITEMS: NavEntry[] = [
  {
    name: "DASHBOARD",
    path: "/dashboard",
    icon: <DashboardIcon {...iconProps} />,
  },
  {
    name: "SALES",
    icon: <Store {...iconProps} />,
    pages: [
      { name: "BASKETS", path: "/baskets" },
      { name: "QUOTES", path: "/quotes", badge: ReviewQuoteBadge },
      { name: "ORDER", path: "/ordrestyring", badge: NewOrderBadge },
      { name: "CUSTOMER", path: "/kunder" },
    ],
  },
  // EE-9: the stage pages below only ever filtered what the Overview can filter
  // itself, so the group collapses to a single link to the Overview.
  {
    name: "MANUFACTURING",
    path: "/production",
    icon: <Build {...iconProps} />,
  },
  // { name: "OVERVIEW", path: "/produktion" },
  // { name: "REVIEW", path: "/review" },
  // { name: "CAM", path: "/cam" },
  // { name: "PRODUCTION", path: "/allProdction" },
  // { name: "MILLING", path: "/milling" },
  // { name: "QC", path: "/qc" },
  // { name: "SHIPPING", path: "/shipping" },
  // { name: "PLANNING", path: "/planning" },
  {
    name: "EQUIPMENT",
    icon: <PrecisionManufacturingIcon {...iconProps} />,
    pages: [
      { name: "TOOLS", path: "/tools" },
      // { name: "HISTORY", path: "/history" },
    ],
  },
  {
    name: "OUTSOURCING",
    icon: <PartnersIcon {...iconProps} />,
    pages: [
      {
        name: "REQUISITIONS",
        path: "/requisitions",
        badge: RequisitionUpdatesBadge,
      },
      { name: "INQUIRIES", path: "/inquiries", badge: InquiryUpdatesBadge },
      { name: "PARTNERS", path: "/partnere" },
    ],
  },
  {
    name: "CONTENT",
    icon: <DashboardCustomize {...iconProps} />,
    pages: [
      { name: "THREADS", path: "/threads" },
      { name: "COUNTRIES", path: "/countries" },
    ],
  },
  {
    name: "SETTINGS",
    icon: <SettingsIcon {...iconProps} />,
    pages: [
      { name: "MASKINER", path: "/maskine" },
      { name: "BRUGERE", path: "/brugere" },
      { name: "CALENDAR", path: "/calendar" },
      { name: "PRODUCTION PRIORITY", path: "/production-priority" },
      { name: "SYSTEM", path: "/settings" },
    ],
  },
];

/** A menu entry is active on its own path and on any detail page below it, so
 *  /ordrestyring/42 keeps ORDER highlighted. */
export const isPathActive = (pathname: string, path: string): boolean =>
  pathname === path || pathname.startsWith(`${path}/`);

/** Name of the group owning the given route, used to auto-expand the menu. */
export const findGroupForPath = (pathname: string): string | null => {
  const group = NAV_ITEMS.find(
    (entry) =>
      isNavGroup(entry) &&
      entry.pages.some((page) => isPathActive(pathname, page.path)),
  );
  return group ? group.name : null;
};
